import { Link, useLocation } from "react-router-dom";
import {
  BadgeDollarSign,
  Bug,
  Camera,
  CalendarCheck,
  FileText,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  MessageSquare,
  Radar,
  Settings,
  Sparkles,
  Trello,
  Upload,
  Users,
} from "lucide-react";
import { cn } from "../lib/utils";
import { logout } from "../lib/firebase";
import { useAppStore } from "../store/useAppStore";
import { Brand } from "./Brand";
import { ReportProblemButton } from "./ReportProblemButton";

const ADMIN_EMAIL = "stephen.tech139@gmail.com";

type NavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
};

type NavSection = {
  label: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    label: "Command center",
    items: [{ name: "Dashboard", href: "/", icon: LayoutDashboard }],
  },
  {
    label: "Acquisition",
    items: [
      { name: "Lead Inbox", href: "/leads", icon: Users },
      { name: "Opportunities", href: "/opportunities", icon: Sparkles },
      { name: "Drive for Dollars", href: "/drive-for-dollars", icon: Camera },
      { name: "Property Map", href: "/map", icon: MapIcon },
    ],
  },
  {
    label: "Pipeline flow",
    items: [
      { name: "Contracts & Docs", href: "/contracts", icon: FileText },
      { name: "Signals & Outreach", href: "/signals", icon: Radar },
      { name: "Chat with Pipeline", href: "/ask", icon: MessageSquare },
      { name: "Pipeline", href: "/pipeline", icon: Trello },
      { name: "Follow-Ups", href: "/follow-ups", icon: CalendarCheck },
      { name: "Import", href: "/import", icon: Upload },
    ],
  },
  {
    label: "Workspace",
    items: [
      { name: "Settings", href: "/settings", icon: Settings },
      { name: "Pricing", href: "/pricing", icon: BadgeDollarSign },
    ],
  },
];

const adminNavItem: NavItem = { name: "Problem Reports", href: "/problem-reports", icon: Bug };

export const navItems = navSections.flatMap((section) => section.items);

type SidebarNavProps = {
  onNavigate?: () => void;
  className?: string;
  linkClassName?: string;
};

function NavLink({ item, onNavigate, linkClassName }: { item: NavItem; onNavigate?: () => void; linkClassName?: string }) {
  const location = useLocation();
  const isActive = location.pathname === item.href || (item.href !== "/" && location.pathname.startsWith(item.href));

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(
        "group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200",
        isActive
          ? "bg-white text-slate-900 shadow-sm shadow-slate-950/20 ring-1 ring-white/70"
          : "text-slate-300 hover:bg-white/8 hover:text-white",
        linkClassName
      )}
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors",
          isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-800 text-slate-300 group-hover:bg-slate-700 group-hover:text-white"
        )}
      >
        <item.icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate font-medium">{item.name}</span>
    </Link>
  );
}

export function SidebarNav({ onNavigate, className, linkClassName }: SidebarNavProps) {
  const { userEmail } = useAppStore();
  const isAdmin = userEmail?.toLowerCase() === ADMIN_EMAIL;
  const sections = isAdmin
    ? [...navSections, { label: "Admin", items: [adminNavItem] }]
    : navSections;

  return (
    <nav className={className ?? "flex-1 px-3 py-3 space-y-5"}>
      {sections.map((section) => (
        <div key={section.label} className="space-y-2">
          <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500/90">{section.label}</p>
          <div className="space-y-1">
            {section.items.map((item) => (
              <NavLink key={item.name} item={item} onNavigate={onNavigate} linkClassName={linkClassName} />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function Sidebar() {
  const { planId } = useAppStore();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <aside className="hidden h-full w-72 shrink-0 flex-col overflow-hidden border-r border-slate-800 bg-slate-950 text-white lg:flex">
      <div className="border-b border-white/10 bg-slate-950 px-5 py-5">
        <Brand variant="logo" className="items-center" imageClassName="h-16 w-auto" />
        <div className="mt-4 flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">LandMiner workspace</p>
            <p className="mt-1 text-sm text-slate-300">Find deals, move leads, and keep follow-up rhythm tight.</p>
          </div>
          <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
            {planId === "free" ? "Free" : planId}
          </span>
        </div>
      </div>
      <SidebarNav />
      <div className="space-y-3 border-t border-white/10 bg-slate-950 px-4 py-4">
        <div className="rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/10 to-emerald-500/10 p-3">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-300">Plan status</p>
          <p className="text-sm text-slate-100">
            {planId === "free"
              ? "Limited access — upgrade to Foundation for AI follow-ups and more capacity."
              : planId === "foundation"
                ? "Foundation active — upgrade to Operator for more throughput."
                : planId === "operator"
                  ? "Operator active — upgrade to Atlas for the deepest workflow."
                  : "Atlas active — the top workflow tier is enabled."}
          </p>
        </div>
        <ReportProblemButton
          buttonLabel="Report a problem"
          triggerVariant="outline"
          className="w-full justify-start border-white/10 bg-white/5 text-slate-100 hover:bg-white/10 hover:text-white"
        />
        <button
          onClick={handleLogout}
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4" />
          Log Out
        </button>
      </div>
    </aside>
  );
}
